import { SelectPropertyValue, Property } from 'api/clients/PropertyClient'
import { sortBy } from 'lodash'
import { makeAutoObservable, observable, ObservableMap } from 'mobx'
import { State } from './State'
import { IWorkflow, UnpersistedPropertyValue, StatePropertyValue } from './Types'
import { v4 as uuid } from 'uuid'

export class Workflow {
  readonly propertyId: IWorkflow['property']['id']
  readonly folder: IWorkflow['folder']
  private _states: ObservableMap<string, State> = observable.map()

  private constructor({ configuration, property, folder }: IWorkflow<Property<StatePropertyValue>>) {
    this.propertyId = property.id
    this.folder = folder
    for (const state in configuration) {
      const propertyValue = property.property_values.find(
        (pv) => `${pv.id}` === state || `${pv.uuid}` === state,
      ) as SelectPropertyValue
      this._states.set(state, State.fromConfig(this, propertyValue, configuration[state]))
    }
    makeAutoObservable(this)
  }

  static fromJSON(json: IWorkflow<Property<StatePropertyValue>>) {
    return new Workflow(json)
  }

  toJSON = () => {
    const json: Record<string, any> = {
      configuration: {},
      property_values: [],
    }
    this._states.forEach((state) => {
      const { propertyValue, ...configuration } = state.toJSON()
      json.configuration[state.key] = configuration
      json.property_values.push(propertyValue)
    })

    return json
  }

  /* ------------------------------- States CRUD ------------------------------ */

  get states() {
    return sortBy(Array.from(this._states.values()), (state) => state.propertyValue.position)
  }

  get groupedStates() {
    return this.states.reduce<State[][]>((groups, state) => {
      if (state.connectedState && groups.length > 0) {
        groups[groups.length - 1].push(state)
      } else {
        groups.push([state])
      }
      return groups
    }, [])
  }

  getState = (id: string) => {
    const state = this._states.get(id)
    if (!state) {
      throw new Error(`State ${id} does not exist`)
    }
    return state
  }

  getNextState = (stateId: string) => {
    const state = this.getState(stateId)
    const index = this.states.indexOf(state)
    if (index < 0 || index === this.states.length - 1) {
      return null
    }
    return this.states[index + 1]
  }

  createState = (propertyValue: Partial<UnpersistedPropertyValue>) => {
    const {
      position = this.states[this.states.length - 1].position + 1,
      value = 'Untitled',
      meta = { color: '#E5ECF3' },
      connected_to_id = null,
      connected_to_uuid = null,
    } = propertyValue

    const newPropertyValue: UnpersistedPropertyValue = {
      uuid: uuid(),
      property_id: this.propertyId,
      value,
      position,
      meta,
      connected_to_id,
      connected_to_uuid,
    }
    const newState = State.fromConfig(this, newPropertyValue)
    this._states.set(newPropertyValue.uuid, newState)
    newState.insertAt(newState.position)
    return newState
  }

  deleteState = (id: string) => {
    this._states.delete(id)
    this.reorderStates()
  }

  reorderStates = () => {
    this.states.forEach((state, i) => state.setPosition(i))
  }

  validate = () => {
    let errors = []
    const duplicateNames = new Set(this.states.map((state) => state.value)).size !== this.states.length

    if (duplicateNames) {
      errors.push('Status names must be unique')
    }

    return errors
  }
}
